import { useSelector } from 'store';
import { useGetOrgQuery, useUpdateOrgMutation } from 'store/slices/apiV1/org';
import { skipToken } from '@reduxjs/toolkit/dist/query/react';
import { handleErr } from 'utils/functions';
import { FieldValues, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import CardSectionWithAction from 'ui-component/cards/CardSectionWithAction';
import {
  Alert,
  AlertTitle,
  FormControl,
  GridLegacy,
  InputAdornment,
  MenuItem,
} from '@mui/material';
import {
  HookFormInput,
  HookFormSelect,
  HookFormToggle,
} from 'ui-component/HookFormComponents';
import SegmentLoadingButton from 'ui-component/Segment/SegmentLoadingButton';
import { ALL_APPS, ALL_FEATURE_IDS, ALL_VIEWS } from 'constants/appConstants';
import { appPermissionAccessLevels } from 'types/apps';
import SegmentButton from 'ui-component/Segment/SegmentButton';
import {
  formConstants,
  formValidation,
} from 'views/procurement/Preferences/constants';
import useSnackbar from 'hooks/useSnackbar';
import {
  InvoiceApprovalRules,
  OrgSupplierConnection,
  UpdatePurchaseLinePricesFromBill,
} from 'types/suppliers';
import _ from 'lodash';
import { gridSpacing } from 'constants/themeConstants';
import { HookFormQuillInput } from 'ui-component/HookFormComponents/HookFormQuillInput';
import { useGetOrgSuppliersServerSideQuery } from 'store/slices/apiV1/suppliers';
import { QueryFilterOperators } from 'types/api';
import { useNavigate } from 'react-router-dom';
import { getDatagridFilterParam } from 'utils/deepLinking';
import {
  InvoiceAggregationTimeframe,
  invoiceAggregationTimeframeLabels,
  PoRequirementType,
  PoRequirementTypeLabels,
} from 'types/org';
import { useAppAccessContext } from 'contexts/AppAccessContext';
import { useIsCofactr } from 'hooks/useIsCofactr';

const ProcurementPreferences = () => {
  const { dispatchSuccessSnackbar, dispatchErrorSnackbar } = useSnackbar();
  const [isEditing, setIsEditing] = useState(false);
  const { activeOrgId } = useSelector((state) => state.org);
  const { isCofactr } = useIsCofactr();

  const { hasFeaturePermission } = useAppAccessContext();
  const hasSupplierBillsFeature = hasFeaturePermission(
    ALL_FEATURE_IDS.SUPPLIER_BILLS
  );

  const navigate = useNavigate();

  const { data: org } = useGetOrgQuery(activeOrgId ?? skipToken);
  const [updateOrg, { isLoading: isUpdatingOrg }] = useUpdateOrgMutation();

  const isProcessingOrg = hasFeaturePermission(ALL_FEATURE_IDS.PROCESSING_ORG);

  const useManagedInventory = org?.useManagedInventory;

  const defaultValues = {
    [formConstants.poFooterNotes.name]: org?.poFooterNotes || null,
    [formConstants.invoiceApproval.name]:
      org?.invoiceApproval || InvoiceApprovalRules.NEVER,
    [formConstants.updatePurchaseLinePricesFromBill.name]:
      org?.updatePurchaseLinePricesFromBill ||
      UpdatePurchaseLinePricesFromBill.FALSE,
    [formConstants.ignoreShippingInBillApproval.name]:
      org?.ignoreShippingInBillApproval || false,
    [formConstants.updatePurchaseShippingFromBill.name]:
      org?.updatePurchaseShippingFromBill || false,
    [formConstants.ignoreDutiesInBillApproval.name]:
      org?.ignoreDutiesInBillApproval || false,
    [formConstants.updatePurchaseDutiesFromBill.name]:
      org?.updatePurchaseDutiesFromBill || false,
    [formConstants.ignoreTaxesInBillApproval.name]:
      org?.ignoreTaxesInBillApproval || false,
    [formConstants.updatePurchaseTaxesFromBill.name]:
      org?.updatePurchaseTaxesFromBill || false,
    [formConstants.onlyUseTotalForBillApproval.name]:
      org?.onlyUseTotalForBillApproval || false,
    [formConstants.requireAdminApprovals.name]:
      org?.requireAdminApprovals || false,
    [formConstants.allowSelfManagedPurchasing.name]:
      org?.allowSelfManagedPurchasing || false,
    [formConstants.allowCofactrManagedPurchasing.name]:
      org?.allowCofactrManagedPurchasing || false,
    [formConstants.allowableCofactrManagedEstimateOverage.name]:
      _.toNumber(org?.allowableCofactrManagedEstimateOverage || '0') * 100 || 0,
    [formConstants.poRequirementType.name]:
      org?.poRequirementType || PoRequirementType.NONE,
    [formConstants.customPoPrefix.name]: org?.customPoPrefix || '',
    [formConstants.customPoNumberLength.name]: org?.customPoNumberLength || 0,
    [formConstants.poRequiredMessage.name]: org?.poRequiredMessage || null,
    [formConstants.autoApproveSupplierQuotes.name]:
      org?.autoApproveSupplierQuotes || false,
    [formConstants.orderApprovalThreshold.name]:
      org?.orderApprovalThreshold || 0,
    [formConstants.aggregateInvoicesByPoNumber.name]:
      org?.aggregateInvoicesByPoNumber ?? true,
    [formConstants.aggregateInvoicesByRequisition.name]:
      org?.aggregateInvoicesByRequisition ?? true,
    [formConstants.aggregateInvoicesBySupplierOrder.name]:
      org?.aggregateInvoicesBySupplierOrder ?? false,
    [formConstants.aggregateInvoicesByTeam.name]:
      org?.aggregateInvoicesByTeam ?? false,
    [formConstants.aggregateInvoicesByProgram.name]:
      org?.aggregateInvoicesByProgram ?? false,
    [formConstants.aggregateInvoicesTimeframe.name]:
      org?.aggregateInvoicesTimeframe || InvoiceAggregationTimeframe.WEEKLY,
    [formConstants.blanketPartsPo.name]: org?.blanketPartsPo || null,
    [formConstants.blanketSubscriptionPo.name]:
      org?.blanketSubscriptionPo || null,
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, errors },
  } = useForm<FieldValues>({
    defaultValues,
    resolver: yupResolver(formValidation),
    mode: 'all',
  });

  useEffect(() => {
    if (org) {
      reset(defaultValues);
    }
  }, [org]);

  const onSubmit = async (data: FieldValues) => {
    if (isDirty) {
      try {
        await updateOrg({
          orgId: activeOrgId!,
          payload: {
            ...data,
            allowableCofactrManagedEstimateOverage: (
              _.toNumber(
                data[formConstants.allowableCofactrManagedEstimateOverage.name]
              ) / 100
            ).toString(),
          },
        }).unwrap();
        dispatchSuccessSnackbar('Procurement preferences updated');
        setIsEditing(false);
      } catch (err) {
        handleErr(err, (errMessage: string) => {
          dispatchErrorSnackbar(errMessage);
        });
      }
    } else {
      setIsEditing(false);
    }
  };

  const allowCofactrManagedPurchasing = useWatch({
    control,
    name: formConstants.allowCofactrManagedPurchasing.name,
  });
  const allowSelfManagedPurchasing =
    useWatch({
      control,
      name: formConstants.allowSelfManagedPurchasing.name,
    }) && isProcessingOrg;

  const allowableCofactrManagedEstimateOverage = useWatch({
    control,
    name: formConstants.allowableCofactrManagedEstimateOverage.name,
  });

  const customPoPrefix = useWatch({
    control,
    name: formConstants.customPoPrefix.name,
  });

  const customPoNumberLength = useWatch({
    control,
    name: formConstants.customPoNumberLength.name,
  });

  const poRequirementType = useWatch({
    control,
    name: formConstants.poRequirementType.name,
  });

  const requireAdminApprovals = useWatch({
    control,
    name: formConstants.requireAdminApprovals.name,
  });

  const { data: directSuppliers } = useGetOrgSuppliersServerSideQuery({
    pageSize: 1,
    schema: ['id'],
    filters: [
      {
        field: 'connectionType',
        operator: QueryFilterOperators['='],
        value: OrgSupplierConnection.DIRECT,
      },
    ],
  });

  const { data: networkSuppliers } = useGetOrgSuppliersServerSideQuery({
    pageSize: 1,
    schema: ['id'],
    filters: [
      {
        field: 'connectionType',
        operator: QueryFilterOperators['='],
        value: OrgSupplierConnection.SUPPLIER_NETWORK,
      },
    ],
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <CardSectionWithAction
        contents={
          <FormControl fullWidth>
            <GridLegacy container spacing={2}>
              {isProcessingOrg && (
                <>
                  <GridLegacy item xs={12}>
                    <HookFormToggle
                      name={formConstants.allowSelfManagedPurchasing.name}
                      label={formConstants.allowSelfManagedPurchasing.label}
                      helperText={
                        formConstants.allowSelfManagedPurchasing.description
                      }
                      errors={errors}
                      control={control}
                      disabled={!isEditing}
                    />
                  </GridLegacy>
                  <GridLegacy item xs={12}>
                    <HookFormToggle
                      name={formConstants.allowCofactrManagedPurchasing.name}
                      label={formConstants.allowCofactrManagedPurchasing.label}
                      helperText={
                        formConstants.allowCofactrManagedPurchasing.description
                      }
                      errors={errors}
                      control={control}
                      disabled={!isEditing}
                    />
                  </GridLegacy>
                </>
              )}
              {!allowCofactrManagedPurchasing &&
                !allowSelfManagedPurchasing && (
                  <GridLegacy item xs={12}>
                    <Alert severity="error">
                      <AlertTitle>No Purchasing Enabled</AlertTitle>
                      You have not enabled any purchasing options. Please enable
                      at least one purchasing option to use Cofactr.
                    </Alert>
                  </GridLegacy>
                )}
              {!allowSelfManagedPurchasing &&
                (directSuppliers?.count || 0) > 0 && (
                  <GridLegacy item xs={12}>
                    <Alert
                      severity="error"
                      action={
                        <SegmentButton
                          color="inherit"
                          size="small"
                          onClick={() =>
                            navigate(
                              `${
                                ALL_VIEWS.SUPPLIERS.path
                              }?${getDatagridFilterParam([
                                {
                                  columnField: 'connectionType',
                                  operatorValue: 'is',
                                  value: OrgSupplierConnection.DIRECT,
                                },
                              ])}`
                            )
                          }
                          permissionScope={{ app: ALL_APPS.SUPPLIERS.id }}
                        >
                          View Direct Suppliers
                        </SegmentButton>
                      }
                    >
                      <AlertTitle>Invalid Direct Suppliers</AlertTitle>
                      You already have {directSuppliers?.count} direct suppliers
                      configured in Cofactr. You must remove them before
                      disabling self-managed purchasing.
                    </Alert>
                  </GridLegacy>
                )}
              {!allowCofactrManagedPurchasing &&
                (networkSuppliers?.count || 0) > 0 && (
                  <GridLegacy item xs={12}>
                    <Alert
                      severity="error"
                      action={
                        <SegmentButton
                          color="inherit"
                          size="small"
                          onClick={() =>
                            navigate(
                              `${
                                ALL_VIEWS.SUPPLIERS.path
                              }?${getDatagridFilterParam([
                                {
                                  columnField: 'connectionType',
                                  operatorValue: 'is',
                                  value: OrgSupplierConnection.SUPPLIER_NETWORK,
                                },
                              ])}`
                            )
                          }
                          permissionScope={{ app: ALL_APPS.SUPPLIERS.id }}
                        >
                          View Managed by Cofactr Suppliers
                        </SegmentButton>
                      }
                    >
                      <AlertTitle>
                        Invalid Managed by Cofactr Suppliers
                      </AlertTitle>
                      You already have {networkSuppliers?.count} Managed by
                      Cofactr suppliers configured in Cofactr. You must remove
                      them before disabling Managed by Cofactr purchasing.
                    </Alert>
                  </GridLegacy>
                )}
              {allowCofactrManagedPurchasing && (
                <GridLegacy item xs={12}>
                  <HookFormInput
                    name={
                      formConstants.allowableCofactrManagedEstimateOverage.name
                    }
                    label={
                      formConstants.allowableCofactrManagedEstimateOverage.label
                    }
                    helperText={
                      formConstants.allowableCofactrManagedEstimateOverage
                        .description
                    }
                    errors={errors}
                    control={control}
                    disabled={!isEditing}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                    }}
                  />
                </GridLegacy>
              )}
              {allowCofactrManagedPurchasing &&
                _.toNumber(allowableCofactrManagedEstimateOverage) < 10 &&
                _.toNumber(allowableCofactrManagedEstimateOverage) > 0 && (
                  <GridLegacy item xs={12}>
                    <Alert severity="warning">
                      <AlertTitle>Increased Risk of Delays</AlertTitle>
                      The Allowable Cofactr-Managed Estimate Overage is less
                      than 10%. Setting a low Allowable Cofactr-Managed Estimate
                      Overage will increase the risk of procurement delays.
                    </Alert>
                  </GridLegacy>
                )}
              {allowCofactrManagedPurchasing &&
                _.toNumber(allowableCofactrManagedEstimateOverage) === 0 && (
                  <GridLegacy item xs={12}>
                    <Alert severity="error">
                      <AlertTitle>High Risk of Delays</AlertTitle>
                      The Allowable Cofactr-Managed Estimate Overage is set to
                      0%. Setting no Allowable Cofactr-Managed Estimate Overage
                      will substantially increase the risk of procurement
                      delays.
                    </Alert>
                  </GridLegacy>
                )}
            </GridLegacy>
          </FormControl>
        }
        action={
          isEditing ? (
            <SegmentLoadingButton
              type="submit"
              variant="contained"
              color="primary"
              size="medium"
              loading={isUpdatingOrg}
              permissionScope={{
                app: ALL_APPS.ORG_SETTINGS.id,
                accessLevel: appPermissionAccessLevels.admin,
              }}
            >
              {isDirty ? 'Save' : 'Cancel'}
            </SegmentLoadingButton>
          ) : (
            <SegmentButton
              type="button"
              variant="contained"
              onClick={() => setIsEditing(true)}
              sx={{ float: 'right' }}
              permissionScope={{
                app: ALL_APPS.ORG_SETTINGS.id,
                accessLevel: appPermissionAccessLevels.admin,
              }}
            >
              Edit
            </SegmentButton>
          )
        }
        title="Managed by Cofactr Purchasing Preferences"
      />
      <CardSectionWithAction
        contents={
          <FormControl fullWidth>
            <GridLegacy container>
              <GridLegacy item xs={12}>
                <HookFormSelect
                  name={formConstants.poRequirementType.name}
                  label={formConstants.poRequirementType.label}
                  control={control}
                  errors={errors}
                  disabled={!isEditing}
                  helperText={formConstants.poRequirementType.description}
                  sx={{ mb: 2 }}
                >
                  {Object.values(PoRequirementType).map((option) => (
                    <MenuItem key={option} value={option}>
                      {PoRequirementTypeLabels[option]}
                    </MenuItem>
                  ))}
                </HookFormSelect>
              </GridLegacy>
              {poRequirementType ===
                PoRequirementType.PURCHASE_REQUEST_EXTERNAL && (
                <GridLegacy item xs={12}>
                  <HookFormInput
                    name={formConstants.poRequiredMessage.name}
                    label={formConstants.poRequiredMessage.label}
                    helperText={formConstants.poRequiredMessage.description}
                    errors={errors}
                    control={control}
                    disabled={!isEditing}
                  />
                </GridLegacy>
              )}
              {allowSelfManagedPurchasing && (
                <GridLegacy item xs={12}>
                  <HookFormInput
                    name={formConstants.customPoPrefix.name}
                    label={formConstants.customPoPrefix.label}
                    helperText={formConstants.customPoPrefix.description}
                    errors={errors}
                    control={control}
                    disabled={!isEditing}
                  />
                </GridLegacy>
              )}
              {allowSelfManagedPurchasing && (
                <GridLegacy item xs={12}>
                  <HookFormInput
                    name={formConstants.customPoNumberLength.name}
                    label={formConstants.customPoNumberLength.label}
                    helperText={formConstants.customPoNumberLength.description}
                    errors={errors}
                    control={control}
                    disabled={!isEditing || !customPoPrefix}
                  />
                </GridLegacy>
              )}
              {!!customPoNumberLength &&
                !!customPoPrefix &&
                allowSelfManagedPurchasing && (
                  <GridLegacy item xs={12}>
                    Example PO Number: {customPoPrefix}
                    {'0'.repeat(customPoNumberLength)}
                  </GridLegacy>
                )}
            </GridLegacy>
          </FormControl>
        }
        action={
          isEditing ? (
            <SegmentLoadingButton
              type="submit"
              variant="contained"
              color="primary"
              size="medium"
              loading={isUpdatingOrg}
              permissionScope={{
                app: ALL_APPS.ORG_SETTINGS.id,
                accessLevel: appPermissionAccessLevels.admin,
              }}
            >
              {isDirty ? 'Save' : 'Cancel'}
            </SegmentLoadingButton>
          ) : (
            <SegmentButton
              type="button"
              variant="contained"
              onClick={() => setIsEditing(true)}
              sx={{ float: 'right' }}
              permissionScope={{
                app: ALL_APPS.ORG_SETTINGS.id,
                accessLevel: appPermissionAccessLevels.admin,
              }}
            >
              Edit
            </SegmentButton>
          )
        }
        title="PO Number Preferences"
        sx={{ mt: gridSpacing }}
      />
      <CardSectionWithAction
        contents={
          <FormControl fullWidth>
            <GridLegacy container spacing={2}>
              <GridLegacy item xs={12}>
                <HookFormToggle
                  name={formConstants.requireAdminApprovals.name}
                  label={formConstants.requireAdminApprovals.label}
                  helperText={formConstants.requireAdminApprovals.description}
                  errors={errors}
                  control={control}
                  disabled={!isEditing}
                />
              </GridLegacy>
              {requireAdminApprovals && (
                <GridLegacy item xs={12}>
                  <HookFormInput
                    name={formConstants.orderApprovalThreshold.name}
                    label={formConstants.orderApprovalThreshold.label}
                    helperText={
                      formConstants.orderApprovalThreshold.description
                    }
                    errors={errors}
                    control={control}
                    disabled={!isEditing}
                  />
                </GridLegacy>
              )}
              <GridLegacy item xs={12}>
                <HookFormToggle
                  name={formConstants.autoApproveSupplierQuotes.name}
                  label={formConstants.autoApproveSupplierQuotes.label}
                  helperText={
                    formConstants.autoApproveSupplierQuotes.description
                  }
                  errors={errors}
                  control={control}
                  disabled={!isEditing}
                />
              </GridLegacy>
            </GridLegacy>
          </FormControl>
        }
        action={
          isEditing ? (
            <SegmentLoadingButton
              type="submit"
              variant="contained"
              color="primary"
              size="medium"
              loading={isUpdatingOrg}
              permissionScope={{
                app: ALL_APPS.ORG_SETTINGS.id,
                accessLevel: appPermissionAccessLevels.admin,
              }}
            >
              {isDirty ? 'Save' : 'Cancel'}
            </SegmentLoadingButton>
          ) : (
            <SegmentButton
              type="button"
              variant="contained"
              onClick={() => setIsEditing(true)}
              sx={{ float: 'right' }}
              permissionScope={{
                app: ALL_APPS.ORG_SETTINGS.id,
                accessLevel: appPermissionAccessLevels.admin,
              }}
            >
              Edit
            </SegmentButton>
          )
        }
        title="Approval Preferences"
        sx={{ mt: gridSpacing }}
      />
      {allowSelfManagedPurchasing && (
        <CardSectionWithAction
          contents={
            <FormControl fullWidth>
              <GridLegacy container spacing={2}>
                <GridLegacy item xs={12}>
                  <HookFormQuillInput
                    name={formConstants.poFooterNotes.name}
                    label={formConstants.poFooterNotes.label}
                    helperText={formConstants.poFooterNotes.description}
                    errors={errors}
                    control={control}
                    disabled={!isEditing}
                  />
                </GridLegacy>
              </GridLegacy>
            </FormControl>
          }
          action={
            isEditing ? (
              <SegmentLoadingButton
                type="submit"
                variant="contained"
                color="primary"
                size="medium"
                loading={isUpdatingOrg}
                permissionScope={{
                  app: ALL_APPS.ORG_SETTINGS.id,
                  accessLevel: appPermissionAccessLevels.admin,
                }}
              >
                {isDirty ? 'Save' : 'Cancel'}
              </SegmentLoadingButton>
            ) : (
              <SegmentButton
                type="button"
                variant="contained"
                onClick={() => setIsEditing(true)}
                sx={{ float: 'right' }}
                permissionScope={{
                  app: ALL_APPS.ORG_SETTINGS.id,
                  accessLevel: appPermissionAccessLevels.admin,
                }}
              >
                Edit
              </SegmentButton>
            )
          }
          title="Purchase Order Preferences"
          sx={{ mt: gridSpacing }}
        />
      )}
      {allowCofactrManagedPurchasing && useManagedInventory && (
        <CardSectionWithAction
          contents={
            <FormControl fullWidth>
              <GridLegacy container spacing={2}>
                <GridLegacy item xs={12}>
                  <HookFormSelect
                    name={formConstants.aggregateInvoicesTimeframe.name}
                    label={formConstants.aggregateInvoicesTimeframe.label}
                    control={control}
                    errors={errors}
                    disabled={!isEditing || !isCofactr}
                    helperText={
                      formConstants.aggregateInvoicesTimeframe.description
                    }
                    sx={{ mb: 2 }}
                  >
                    {Object.values(InvoiceAggregationTimeframe).map(
                      (option) => (
                        <MenuItem key={option} value={option}>
                          {invoiceAggregationTimeframeLabels[option]}
                        </MenuItem>
                      )
                    )}
                  </HookFormSelect>
                </GridLegacy>
                <GridLegacy item xs={12}>
                  <HookFormToggle
                    name={formConstants.aggregateInvoicesByPoNumber.name}
                    label={formConstants.aggregateInvoicesByPoNumber.label}
                    helperText={
                      formConstants.aggregateInvoicesByPoNumber.description
                    }
                    errors={errors}
                    control={control}
                    disabled={!isEditing}
                  />
                </GridLegacy>
                <GridLegacy item xs={12}>
                  <HookFormToggle
                    name={formConstants.aggregateInvoicesByRequisition.name}
                    label={formConstants.aggregateInvoicesByRequisition.label}
                    helperText={
                      formConstants.aggregateInvoicesByRequisition.description
                    }
                    errors={errors}
                    control={control}
                    disabled={!isEditing}
                  />
                </GridLegacy>
                <GridLegacy item xs={12}>
                  <HookFormToggle
                    name={formConstants.aggregateInvoicesBySupplierOrder.name}
                    label={formConstants.aggregateInvoicesBySupplierOrder.label}
                    helperText={
                      formConstants.aggregateInvoicesBySupplierOrder.description
                    }
                    errors={errors}
                    control={control}
                    disabled={!isEditing}
                  />
                </GridLegacy>
                <GridLegacy item xs={12}>
                  <HookFormToggle
                    name={formConstants.aggregateInvoicesByTeam.name}
                    label={formConstants.aggregateInvoicesByTeam.label}
                    helperText={
                      formConstants.aggregateInvoicesByTeam.description
                    }
                    errors={errors}
                    control={control}
                    disabled={!isEditing}
                  />
                </GridLegacy>
                <GridLegacy item xs={12}>
                  <HookFormToggle
                    name={formConstants.aggregateInvoicesByProgram.name}
                    label={formConstants.aggregateInvoicesByProgram.label}
                    helperText={
                      formConstants.aggregateInvoicesByProgram.description
                    }
                    errors={errors}
                    control={control}
                    disabled={!isEditing}
                  />
                </GridLegacy>
                <GridLegacy item xs={12}>
                  <HookFormInput
                    name={formConstants.blanketPartsPo.name}
                    label={formConstants.blanketPartsPo.label}
                    helperText={formConstants.blanketPartsPo.description}
                    errors={errors}
                    control={control}
                    disabled={!isEditing}
                  />
                </GridLegacy>
                <GridLegacy item xs={12}>
                  <HookFormInput
                    name={formConstants.blanketSubscriptionPo.name}
                    label={formConstants.blanketSubscriptionPo.label}
                    helperText={formConstants.blanketSubscriptionPo.description}
                    errors={errors}
                    control={control}
                    disabled={!isEditing}
                  />
                </GridLegacy>
              </GridLegacy>
            </FormControl>
          }
          action={
            isEditing ? (
              <SegmentLoadingButton
                type="submit"
                variant="contained"
                color="primary"
                size="medium"
                loading={isUpdatingOrg}
                permissionScope={{
                  app: ALL_APPS.ORG_SETTINGS.id,
                  accessLevel: appPermissionAccessLevels.admin,
                }}
              >
                {isDirty ? 'Save' : 'Cancel'}
              </SegmentLoadingButton>
            ) : (
              <SegmentButton
                type="button"
                variant="contained"
                onClick={() => setIsEditing(true)}
                sx={{ float: 'right' }}
                permissionScope={{
                  app: ALL_APPS.ORG_SETTINGS.id,
                  accessLevel: appPermissionAccessLevels.admin,
                }}
              >
                Edit
              </SegmentButton>
            )
          }
          title="Cofactr Invoice Preferences"
          sx={{ mt: gridSpacing }}
        />
      )}
      {hasSupplierBillsFeature && (
        <CardSectionWithAction
          contents={
            <FormControl fullWidth>
              <GridLegacy container spacing={2}>
                <GridLegacy item xs={12}>
                  <HookFormSelect
                    name={formConstants.invoiceApproval.name}
                    label={formConstants.invoiceApproval.label}
                    helperText={formConstants.invoiceApproval.description}
                    errors={errors}
                    control={control}
                    disabled={!isEditing}
                  >
                    {Object.values(InvoiceApprovalRules)
                      .filter((o) => o !== InvoiceApprovalRules.DEFAULT)
                      .map((rule) => (
                        <MenuItem value={rule} key={rule}>
                          {_.startCase(rule)}
                        </MenuItem>
                      ))}
                  </HookFormSelect>
                </GridLegacy>
                <GridLegacy item xs={12}>
                  <HookFormSelect
                    name={formConstants.updatePurchaseLinePricesFromBill.name}
                    label={formConstants.updatePurchaseLinePricesFromBill.label}
                    helperText={
                      formConstants.updatePurchaseLinePricesFromBill.description
                    }
                    errors={errors}
                    control={control}
                    disabled={!isEditing}
                  >
                    {Object.values(UpdatePurchaseLinePricesFromBill)
                      .filter(
                        (o) => o !== UpdatePurchaseLinePricesFromBill.DEFAULT
                      )
                      .map((rule) => (
                        <MenuItem value={rule} key={rule}>
                          {_.startCase(rule)}
                        </MenuItem>
                      ))}
                  </HookFormSelect>
                </GridLegacy>
                <GridLegacy item xs={12}>
                  <HookFormToggle
                    name={formConstants.onlyUseTotalForBillApproval.name}
                    label={formConstants.onlyUseTotalForBillApproval.label}
                    helperText={
                      formConstants.onlyUseTotalForBillApproval.description
                    }
                    errors={errors}
                    control={control}
                    disabled={!isEditing}
                  />
                </GridLegacy>
                <GridLegacy item xs={12}>
                  <HookFormToggle
                    name={formConstants.ignoreShippingInBillApproval.name}
                    label={formConstants.ignoreShippingInBillApproval.label}
                    helperText={
                      formConstants.ignoreShippingInBillApproval.description
                    }
                    errors={errors}
                    control={control}
                    disabled={!isEditing}
                  />
                </GridLegacy>
                <GridLegacy item xs={12}>
                  <HookFormToggle
                    name={formConstants.updatePurchaseShippingFromBill.name}
                    label={formConstants.updatePurchaseShippingFromBill.label}
                    helperText={
                      formConstants.updatePurchaseShippingFromBill.description
                    }
                    errors={errors}
                    control={control}
                    disabled={!isEditing}
                  />
                </GridLegacy>
                <GridLegacy item xs={12}>
                  <HookFormToggle
                    name={formConstants.ignoreDutiesInBillApproval.name}
                    label={formConstants.ignoreDutiesInBillApproval.label}
                    helperText={
                      formConstants.ignoreDutiesInBillApproval.description
                    }
                    errors={errors}
                    control={control}
                    disabled={!isEditing}
                  />
                </GridLegacy>
                <GridLegacy item xs={12}>
                  <HookFormToggle
                    name={formConstants.updatePurchaseDutiesFromBill.name}
                    label={formConstants.updatePurchaseDutiesFromBill.label}
                    helperText={
                      formConstants.updatePurchaseDutiesFromBill.description
                    }
                    errors={errors}
                    control={control}
                    disabled={!isEditing}
                  />
                </GridLegacy>
                <GridLegacy item xs={12}>
                  <HookFormToggle
                    name={formConstants.ignoreTaxesInBillApproval.name}
                    label={formConstants.ignoreTaxesInBillApproval.label}
                    helperText={
                      formConstants.ignoreTaxesInBillApproval.description
                    }
                    errors={errors}
                    control={control}
                    disabled={!isEditing}
                  />
                </GridLegacy>
                <GridLegacy item xs={12}>
                  <HookFormToggle
                    name={formConstants.updatePurchaseTaxesFromBill.name}
                    label={formConstants.updatePurchaseTaxesFromBill.label}
                    helperText={
                      formConstants.updatePurchaseTaxesFromBill.description
                    }
                    errors={errors}
                    control={control}
                    disabled={!isEditing}
                  />
                </GridLegacy>
              </GridLegacy>
            </FormControl>
          }
          action={
            isEditing ? (
              <SegmentLoadingButton
                type="submit"
                variant="contained"
                color="primary"
                size="medium"
                loading={isUpdatingOrg}
                permissionScope={{
                  app: ALL_APPS.PROCUREMENT.id,
                  accessLevel: appPermissionAccessLevels.admin,
                }}
              >
                {isDirty ? 'Save' : 'Cancel'}
              </SegmentLoadingButton>
            ) : (
              <SegmentButton
                type="button"
                variant="contained"
                onClick={() => setIsEditing(true)}
                sx={{ float: 'right' }}
                permissionScope={{
                  app: ALL_APPS.PROCUREMENT.id,
                  accessLevel: appPermissionAccessLevels.admin,
                }}
              >
                Edit
              </SegmentButton>
            )
          }
          title="Supplier Bill Preferences"
          sx={{ mt: gridSpacing }}
        />
      )}
    </form>
  );
};

export default ProcurementPreferences;
